'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis } from 'recharts';

const TOOLTIP_STYLE = { backgroundColor: '#111827', border: '1px solid #1E2D4A', borderRadius: '6px', color: '#E8EDF5', fontSize: '11px' };

const ACCURACY_HISTORY = [
  { month: 'Oct', ml: 68.2, baseline: 52.1, market: 55.3 },
  { month: 'Nov', ml: 70.1, baseline: 51.8, market: 56.1 },
  { month: 'Dec', ml: 71.4, baseline: 52.4, market: 55.8 },
  { month: 'Jan', ml: 72.8, baseline: 53.0, market: 57.2 },
  { month: 'Feb', ml: 73.5, baseline: 52.7, market: 56.9 },
  { month: 'Mar', ml: 74.1, baseline: 53.2, market: 57.5 },
  { month: 'Apr', ml: 74.8, baseline: 52.9, market: 57.8 },
  { month: 'May', ml: 75.3, baseline: 53.5, market: 58.1 },
];

const MODEL_CALIBRATION = [
  { confidence: '50-60%', predicted: 55, actual: 54.2 },
  { confidence: '60-70%', predicted: 65, actual: 63.8 },
  { confidence: '70-80%', predicted: 75, actual: 73.1 },
  { confidence: '80-90%', predicted: 85, actual: 82.4 },
  { confidence: '90-100%', predicted: 95, actual: 91.7 },
];

const DATA_SOURCES = [
  { name: 'NHL Official API', status: 'live', uptime: 99.8, latency: 142, quality: 98.2, lastUpdate: '12s ago' },
  { name: 'Stats Engine', status: 'live', uptime: 99.9, latency: 89, quality: 99.1, lastUpdate: '8s ago' },
  { name: 'Injury Reports', status: 'live', uptime: 98.4, latency: 312, quality: 94.7, lastUpdate: '2m ago' },
  { name: 'Odds Feed', status: 'degraded', uptime: 96.2, latency: 891, quality: 87.3, lastUpdate: '8m ago' },
  { name: 'Historical DB', status: 'live', uptime: 100, latency: 45, quality: 99.9, lastUpdate: '1s ago' },
];

const MODEL_PERFORMANCE = [
  { model: 'Ensemble ML', accuracy: 75.3, roi: 8.4, brier: 0.218, calibration: 94.1 },
  { model: 'XGBoost', accuracy: 73.1, roi: 6.9, brier: 0.231, calibration: 91.8 },
  { model: 'Neural Net', accuracy: 72.8, roi: 6.5, brier: 0.235, calibration: 90.4 },
  { model: 'Random Forest', accuracy: 71.4, roi: 5.8, brier: 0.244, calibration: 88.7 },
  { model: 'Logistic Reg.', accuracy: 68.2, roi: 3.2, brier: 0.267, calibration: 85.1 },
];

const RADAR_DATA = [
  { metric: 'Précision', value: 75 },
  { metric: 'Calibration', value: 94 },
  { metric: 'Couverture', value: 88 },
  { metric: 'Fraîcheur', value: 96 },
  { metric: 'Stabilité', value: 82 },
  { metric: 'ROI', value: 71 },
];

const SYSTEM_LIMITS = [
  { limit: 'Délai données live', value: '~30 secondes', severity: 'info' },
  { limit: 'Projections gardiens', value: 'Dépend confirmations officielles', severity: 'warning' },
  { limit: 'Données blessures', value: 'Basées sur rapports publics', severity: 'warning' },
  { limit: 'Cotes sportives', value: 'Données partielles (API limitée)', severity: 'error' },
  { limit: 'Historique matchups', value: '5 dernières saisons', severity: 'info' },
  { limit: 'Précision max théorique', value: '~78% (variance inhérente)', severity: 'info' },
];

export default function TrustDashboardClient() {
  const [activeTab, setActiveTab] = useState<'accuracy' | 'calibration' | 'data' | 'limits'>('accuracy');

  return (
    <AppLayout>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Icon name="ShieldCheckIcon" size={22} className="text-positive" />
              Trust & Transparency Dashboard
            </h1>
            <p className="text-sm text-muted-foreground mt-0.5">Précision historique · Calibration modèles · Qualité données · Limites système</p>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-positive-subtle border border-positive/20">
            <span className="w-2 h-2 rounded-full bg-positive pulse-dot" />
            <span className="text-xs font-medium text-positive font-mono-data">LIVE MONITORING</span>
          </div>
        </div>

        {/* KPI Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Précision ML', value: '75.3%', sub: '+7.1% vs baseline', color: 'text-positive', icon: 'ChartBarIcon' },
            { label: 'Calibration Score', value: '94.1%', sub: 'Excellent', color: 'text-primary', icon: 'AdjustmentsHorizontalIcon' },
            { label: 'Uptime Données', value: '99.2%', sub: '30j rolling', color: 'text-positive', icon: 'ServerIcon' },
            { label: 'ROI Moyen', value: '+8.4%', sub: 'Sur 1,247 prédictions', color: 'text-warning', icon: 'CurrencyDollarIcon' },
          ].map((kpi) => (
            <div key={kpi.label} className="card p-4">
              <div className="flex items-center gap-2 mb-2">
                <Icon name={kpi.icon as any} size={16} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{kpi.label}</span>
              </div>
              <div className={`text-2xl font-bold font-mono-data ${kpi.color}`}>{kpi.value}</div>
              <div className="text-xs text-muted-foreground mt-1">{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-card border border-border rounded-lg p-1 w-fit">
          {(['accuracy', 'calibration', 'data', 'limits'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${activeTab === tab ? 'bg-primary text-white' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {tab === 'accuracy' ? 'Précision Historique' : tab === 'calibration' ? 'Calibration' : tab === 'data' ? 'Qualité Données' : 'Limites Système'}
            </button>
          ))}
        </div>

        {/* Accuracy Tab */}
        {activeTab === 'accuracy' && (
          <div className="space-y-4">
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-foreground mb-4">Précision ML vs Baseline vs Marché (8 mois)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={ACCURACY_HISTORY}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <YAxis domain={[48, 80]} tick={{ fontSize: 11, fill: '#6B7280' }} unit="%" />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: any) => [`${v}%`]} />
                  <Line type="monotone" dataKey="ml" stroke="#00D4FF" strokeWidth={2.5} dot={{ r: 4 }} name="NHL Analytics ML" />
                  <Line type="monotone" dataKey="market" stroke="#FFB800" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Marché" />
                  <Line type="monotone" dataKey="baseline" stroke="#6B7280" strokeWidth={1} strokeDasharray="2 2" dot={false} name="Baseline" />
                </LineChart>
              </ResponsiveContainer>
              <div className="flex gap-6 mt-3 justify-center">
                {[{ color: '#00D4FF', label: 'NHL Analytics ML' }, { color: '#FFB800', label: 'Marché' }, { color: '#6B7280', label: 'Baseline' }].map((l) => (
                  <div key={l.label} className="flex items-center gap-1.5">
                    <div className="w-3 h-0.5 rounded" style={{ backgroundColor: l.color }} />
                    <span className="text-xs text-muted-foreground">{l.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="card p-5">
              <h3 className="text-sm font-semibold text-foreground mb-4">Performance par Modèle</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-2 text-muted-foreground font-medium">Modèle</th>
                      <th className="text-right py-2 text-muted-foreground font-medium">Précision</th>
                      <th className="text-right py-2 text-muted-foreground font-medium">ROI</th>
                      <th className="text-right py-2 text-muted-foreground font-medium">Brier Score</th>
                      <th className="text-right py-2 text-muted-foreground font-medium">Calibration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {MODEL_PERFORMANCE.map((m, i) => (
                      <tr key={m.model} className="border-b border-border/50 hover:bg-card-hover">
                        <td className="py-2.5 font-medium text-foreground flex items-center gap-2">
                          {i === 0 && <span className="text-2xs bg-positive-subtle text-positive px-1.5 py-0.5 rounded font-bold">BEST</span>}
                          {m.model}
                        </td>
                        <td className="py-2.5 text-right text-positive font-mono-data">{m.accuracy}%</td>
                        <td className="py-2.5 text-right text-warning font-mono-data">+{m.roi}%</td>
                        <td className="py-2.5 text-right text-muted-foreground font-mono-data">{m.brier}</td>
                        <td className="py-2.5 text-right text-primary font-mono-data">{m.calibration}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Calibration Tab */}
        {activeTab === 'calibration' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-foreground mb-4">Calibration : Confiance Prédite vs Réelle</h3>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={MODEL_CALIBRATION} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1E2D4A" />
                  <XAxis dataKey="confidence" tick={{ fontSize: 10, fill: '#6B7280' }} />
                  <YAxis domain={[40, 100]} tick={{ fontSize: 11, fill: '#6B7280' }} unit="%" />
                  <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v: any) => [`${v}%`]} />
                  <Bar dataKey="predicted" fill="#1E2D4A" name="Confiance prédite" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="actual" fill="#00D4FF" name="Résultat réel" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
              <p className="text-xs text-muted-foreground mt-3 text-center">Un modèle parfaitement calibré = barres identiques. Écart moyen : 2.1%</p>
            </div>
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-foreground mb-4">Score Global du Système</h3>
              <ResponsiveContainer width="100%" height={260}>
                <RadarChart data={RADAR_DATA}>
                  <PolarGrid stroke="#1E2D4A" />
                  <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: '#6B7280' }} />
                  <Radar name="Score" dataKey="value" stroke="#00D4FF" fill="#00D4FF" fillOpacity={0.15} strokeWidth={2} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Data Quality Tab */}
        {activeTab === 'data' && (
          <div className="card p-5">
            <h3 className="text-sm font-semibold text-foreground mb-4">Sources de Données — Qualité & Disponibilité</h3>
            <div className="space-y-3">
              {DATA_SOURCES.map((src) => (
                <div key={src.name} className="flex items-center gap-4 p-4 rounded-xl bg-background border border-border">
                  <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${src.status === 'live' ? 'bg-positive pulse-dot' : src.status === 'degraded' ? 'bg-warning' : 'bg-negative'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-foreground">{src.name}</div>
                    <div className="text-xs text-muted-foreground">Dernière mise à jour : {src.lastUpdate}</div>
                  </div>
                  <div className="grid grid-cols-3 gap-6 text-right">
                    <div>
                      <div className="text-xs text-muted-foreground">Uptime</div>
                      <div className="text-sm font-mono-data text-foreground">{src.uptime}%</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Latence</div>
                      <div className={`text-sm font-mono-data ${src.latency > 500 ? 'text-warning' : 'text-positive'}`}>{src.latency}ms</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Qualité</div>
                      <div className={`text-sm font-mono-data ${src.quality >= 95 ? 'text-positive' : src.quality >= 90 ? 'text-warning' : 'text-negative'}`}>{src.quality}%</div>
                    </div>
                  </div>
                  <span className={`text-2xs px-2 py-1 rounded font-bold ${src.status === 'live' ? 'bg-positive-subtle text-positive' : src.status === 'degraded' ? 'bg-warning/10 text-warning' : 'bg-negative-subtle text-negative'}`}>
                    {src.status.toUpperCase()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Limits Tab */}
        {activeTab === 'limits' && (
          <div className="space-y-4">
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-foreground mb-2">Limites & Transparence du Système</h3>
              <p className="text-xs text-muted-foreground mb-4">NHL Analytics Pro s'engage à une transparence totale sur les capacités et limites de la plateforme.</p>
              <div className="space-y-2">
                {SYSTEM_LIMITS.map((l) => (
                  <div key={l.limit} className={`flex items-start gap-3 p-4 rounded-xl border ${
                    l.severity === 'error' ? 'border-negative/20 bg-negative-subtle/10' :
                    l.severity === 'warning'? 'border-warning/20 bg-warning/5' : 'border-border bg-background'
                  }`}>
                    <Icon
                      name={l.severity === 'error' ? 'ExclamationCircleIcon' : l.severity === 'warning' ? 'ExclamationTriangleIcon' : 'InformationCircleIcon'}
                      size={16}
                      className={l.severity === 'error' ? 'text-negative mt-0.5' : l.severity === 'warning' ? 'text-warning mt-0.5' : 'text-primary mt-0.5'}
                    />
                    <div>
                      <div className="text-sm font-medium text-foreground">{l.limit}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">{l.value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card p-5 border-primary/20 bg-primary/5">
              <div className="flex items-start gap-3">
                <Icon name="ShieldCheckIcon" size={20} className="text-primary mt-0.5" />
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Notre engagement de transparence</h4>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    NHL Analytics Pro ne garantit aucun résultat financier. Les projections sont des outils analytiques basés sur des modèles statistiques. 
                    La précision historique de 75.3% ne prédit pas les performances futures. Utilisez ces données comme aide à la décision, pas comme certitude.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
